// Desktop Dev Chat Header - model+btn, context options, chats, progress (last when loading)

import React from 'react';
import { RevoltButton, RevoltIcon } from '../../../../../components';
import { DesktopDevChatContextOptions } from './DesktopDevChatContextOptions';
import { DesktopDevChatModelPicker } from './DesktopDevChatModelPicker';
import { DesktopDevChatChatsPicker } from './DesktopDevChatChatsPicker';
import './DesktopDevChatHeader.css';

function formatMetricSlashPair(left, right) {
  const l = left != null && left !== '' && !Number.isNaN(Number(left)) ? Number(left).toLocaleString() : null;
  const r = right != null && right !== '' && !Number.isNaN(Number(right)) ? Number(right).toLocaleString() : null;
  if (l != null && r != null) return `${l} / ${r}`;
  if (l != null) return l;
  if (r != null) return r;
  return '—';
}

export function DesktopDevChatHeader({
  models = [],
  selectedModelId,
  modelState,
  loadProgress = 0,
  statusMessage = null,
  statusType = 'info', // 'info' | 'success' | 'error'
  /** Active LlamaContext window (tokens) */
  contextRuntimeTokens = null,
  /** Model trainContextSize from GGUF (tokens) */
  contextTrainTokens = null,
  /** LlamaModel.gpuLayers — layers offloaded to GPU */
  layerGpuCount = null,
  /** GgufInsights.totalLayers — layer count in file */
  layerTotalCount = null,
  onSelectModel,
  onLoad,
  onUnload,
  contextSizeMode = 'auto',
  contextSizeMin = 2048,
  contextSizeMax = 4096,
  contextAllocationMode = 'auto',
  contextAllocationSize = 4096,
  onContextSizeModeChange,
  onContextSizeMinChange,
  onContextSizeMaxChange,
  onContextAllocationModeChange,
  onContextAllocationSizeChange,
  defaultContextFlashAttention = false,
  onDefaultContextFlashAttentionChange,
  onContextOptionsCommit,
  chats = [],
  activeChatId,
  onSelectChat,
  onCreateChat,
  onDeleteChat,
}) {
  const isLoading = modelState === 'loading';
  const isLoaded = modelState === 'loaded';
  const isUnloading = modelState === 'unloading';

  const showPlay = !isLoaded;
  const canClick = showPlay
    ? !isLoading && !isUnloading && !!selectedModelId
    : isLoaded && !isUnloading;

  const handleClick = showPlay ? onLoad : onUnload;

  const hasContextTokenBadge =
    isLoaded && (contextRuntimeTokens != null || contextTrainTokens != null);
  const hasLayersBadge =
    isLoaded && (layerGpuCount != null || layerTotalCount != null);
  const hasAnyModelBadge = hasContextTokenBadge || hasLayersBadge;
  const showProgressRow =
    isLoading || isUnloading || loadProgress > 0 || statusMessage || hasAnyModelBadge;
  const showProgressBar = isLoading || loadProgress > 0;

  return (
    <div className="desktop-dev-chat-header">
      {/* Row 1: Model dropdown + Load button */}
      <div className="desktop-dev-chat-header-row desktop-dev-chat-header-row-1">
        <div className="desktop-dev-chat-header-model">
          <DesktopDevChatModelPicker
            models={models}
            selectedModelId={selectedModelId}
            onSelectModel={onSelectModel}
            disabled={isLoading || isUnloading}
          />
        </div>
        <RevoltButton
          variant={showPlay ? 'primary' : 'outline'}
          size="default"
          onClick={handleClick}
          disabled={!canClick}
          className="desktop-dev-chat-header-btn desktop-dev-chat-header-toggle-btn"
          title={showPlay ? 'Load model' : 'Unload model'}
        >
          <RevoltIcon
            key={showPlay ? 'play' : 'stop'}
            name={showPlay ? 'lucide:play' : 'square'}
            size="sm"
          />
        </RevoltButton>
      </div>

      {/* Row 2: Context size + Context allocation */}
      <div className="desktop-dev-chat-header-row desktop-dev-chat-header-row-2">
        <DesktopDevChatContextOptions
          contextSizeMode={contextSizeMode}
          contextSizeMin={contextSizeMin}
          contextSizeMax={contextSizeMax}
          contextAllocationMode={contextAllocationMode}
          contextAllocationSize={contextAllocationSize}
          onContextSizeModeChange={onContextSizeModeChange}
          onContextSizeMinChange={onContextSizeMinChange}
          onContextSizeMaxChange={onContextSizeMaxChange}
          onContextAllocationModeChange={onContextAllocationModeChange}
          onContextAllocationSizeChange={onContextAllocationSizeChange}
          defaultContextFlashAttention={defaultContextFlashAttention}
          onDefaultContextFlashAttentionChange={onDefaultContextFlashAttentionChange}
          onContextOptionsCommit={onContextOptionsCommit}
          disabled={isLoading || isUnloading}
        />
      </div>

      {/* Row 3: Chats button (right-aligned) */}
      <div className="desktop-dev-chat-header-row desktop-dev-chat-header-row-3">
        <div className="desktop-dev-chat-header-row-3-actions">
          <DesktopDevChatChatsPicker
            chats={chats}
            activeChatId={activeChatId}
            onSelectChat={onSelectChat}
            onCreateChat={onCreateChat}
            onDeleteChat={onDeleteChat}
            disabled={isLoading || isUnloading}
          />
        </div>
      </div>

      {/* Row 4: Progress bar (last, when loading/unloading) */}
      {showProgressRow && (
        <div className="desktop-dev-chat-header-row desktop-dev-chat-header-row-4">
          <div className="desktop-dev-chat-header-progress-status">
            {showProgressBar && (
              <div className="desktop-dev-chat-header-progress-bar">
                <div className="desktop-dev-chat-header-progress-track">
                  <div
                    className="desktop-dev-chat-header-progress-fill"
                    style={{ width: `${Math.min(100, Math.max(0, loadProgress))}%` }}
                  />
                </div>
                <span className="desktop-dev-chat-header-progress-label">
                  {loadProgress}%
                </span>
              </div>
            )}
            {(statusMessage || (isUnloading && 'Unloading…')) && (
              <span
                className={`desktop-dev-chat-header-status desktop-dev-chat-header-status--${
                  statusMessage ? statusType : 'info'
                }`}
              >
                {statusMessage || 'Unloading…'}
              </span>
            )}
            {hasAnyModelBadge && (
              <div className="desktop-dev-chat-header-badge-row">
                {hasContextTokenBadge && (
                  <div
                    className="desktop-dev-chat-metric-badge"
                    title="Top: runtime context allocated now. Bottom: train context length from GGUF (slash-separated)."
                  >
                    <div className="desktop-dev-chat-metric-badge__title">Context size</div>
                    <div className="desktop-dev-chat-metric-badge__values">
                      {formatMetricSlashPair(contextRuntimeTokens, contextTrainTokens)}
                    </div>
                  </div>
                )}
                {hasLayersBadge && (
                  <div
                    className="desktop-dev-chat-metric-badge"
                    title="Top: label. Bottom: GPU-offloaded layer count / total layers in GGUF."
                  >
                    <div className="desktop-dev-chat-metric-badge__title">GPU layers</div>
                    <div className="desktop-dev-chat-metric-badge__values">
                      {formatMetricSlashPair(layerGpuCount, layerTotalCount)}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
